import React, { useContext, useState, useEffect } from "react";
import {
  Box, Typography, IconButton, Pagination, Paper, Stack,
  Tooltip, Fade, useTheme, alpha, Divider, Avatar, Chip, Badge,
} from "@mui/material";
import {
  Visibility, LocationOn, Search, RestartAlt,
  VolunteerActivism,
} from "@mui/icons-material";
import { useQuery } from "react-query";

import DynamicButton from "../../components/DynamicButton/DynamicButton";
import ClientList from "../../components/ClientList/ClientList";
import { QK } from "../../base/qk";
import { fetchClients } from "../../api/services/Client";
import MyContext from "../../context/MyContext";
import CustomInput from "../../components/CustomForm/CustomInput";
import ClientsScreenSkeleton from "./skeleton/ClientsScreenSkeleton";
import ClientListSkeleton from "./skeleton/ClientListSkeleton";

const ClientsScreen = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchClient, setSearchClient] = useState("");
  const [searchCandidate, setSearchCandidate] = useState("");
  const [debouncedSearchClient, setDebouncedSearchClient] = useState("");
  const [debouncedSearchCandidate, setDebouncedSearchCandidate] = useState("");
  const [filterByLocation, setFilterByLocation] = useState(true);
  const [carePlanFilter, setCarePlanFilter] = useState("all");
  
  const theme = useTheme();
  const { locationID } = useContext(MyContext);
  const locationFilter = filterByLocation ? locationID : null;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchClient(searchClient);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchClient]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchCandidate(searchCandidate);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchCandidate]);

  const { data, isLoading, isFetching } = useQuery(
    [QK.clients, currentPage, debouncedSearchClient, debouncedSearchCandidate, locationFilter, carePlanFilter],
    () => fetchClients(currentPage, debouncedSearchClient, debouncedSearchCandidate, locationFilter, carePlanFilter),
    { 
      keepPreviousData: true, 
      enabled: !!currentPage,
      onSuccess: (res) => {
        if (res?.results?.length === 0 && res?.count > 0 && currentPage > 1) {
          setCurrentPage(1);
        }
      }
    }
  );

  const totalRecords = data?.count || 0;
  const pageSize = 10;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterClick = (type) => { 
    setCarePlanFilter(type); 
    setCurrentPage(1);
  };

  const toggleLocationFilter = () => { 
    setFilterByLocation((prev) => !prev); 
    setCurrentPage(1);
  };

  const StatusCircleIcon = ({ type, color, tooltip }) => {
    const showCount = carePlanFilter === type;
    return (
      <Tooltip title={tooltip} arrow>
        <Box 
          onClick={() => handleFilterClick(type)}
          sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', px: 0.5 }}
        >
          <Badge 
            badgeContent={showCount ? totalRecords : 0} 
            color="default"
            invisible={!showCount || totalRecords === 0}
            sx={{ "& .MuiBadge-badge": { fontSize: '0.6rem', height: 16, minWidth: 16, fontWeight: 800, bgcolor: '#fff', color: color, border: `1px solid ${color}` }}}
          >
            <Avatar sx={{ 
                width: 36, height: 36, 
                bgcolor: carePlanFilter === type ? color : alpha(color, 0.1), 
                color: carePlanFilter === type ? '#fff' : color,
                transition: '0.2s', border: `1px solid ${alpha(color, 0.1)}`
            }}>
              <VolunteerActivism sx={{ fontSize: 20 }} />
            </Avatar>
          </Badge>
          <Box sx={{ width: '100%', height: 3, mt: 0.5, bgcolor: carePlanFilter === type ? color : 'transparent' }} />
        </Box>
      </Tooltip>
    );
  };

  if (isLoading && !data) return <ClientsScreenSkeleton />;

  return (
    <Fade in timeout={800}>
      <Box sx={{ 
        display: "flex", flexDirection: "column", height: "100vh", p: 1.5, gap: 1.5, 
        background: theme.palette.background.default, maxWidth: '100vw', overflow: 'hidden' 
      }}>
        
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Typography variant="h6" fontWeight={800} noWrap>Service Users</Typography>
              <Chip label={`${totalRecords} Total`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
            </Stack>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1, md: 2 }, flexGrow: 1, justifyContent: 'flex-end' }}>
              
              <Box sx={{ 
                minWidth: '180px', 
                maxWidth: '250px', 
                flexGrow: 1
              }}>
                <CustomInput
                  placeholder="Search Service users..."
                  value={searchClient}
                  onChange={(e) => setSearchClient(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.divider, 0.05),
                      '& fieldset': { borderColor: alpha(theme.palette.divider, 0.1) },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.85rem',
                      padding: '6px 12px'
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Search fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                    )
                  }}
                />
              </Box>

              <Box sx={{ 
                minWidth: '180px', 
                maxWidth: '250px', 
                flexGrow: 1
              }}>
                <CustomInput
                  placeholder="Search candidates..."
                  value={searchCandidate}
                  onChange={(e) => setSearchCandidate(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.divider, 0.05),
                      '& fieldset': { borderColor: alpha(theme.palette.divider, 0.1) },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.85rem',
                      padding: '6px 12px'
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Search fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                    )
                  }}
                />
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

              <Stack direction="row" spacing={1}>
                <StatusCircleIcon type="no_outstanding" color="#2dd4bf" tooltip="Compliant" />
                <StatusCircleIcon type="due_soon" color="#fbbf24" tooltip="Due Soon" />
                <StatusCircleIcon type="outstanding" color="#f43f5e" tooltip="Outstanding" />
              </Stack>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Reset"><IconButton size="small" onClick={() => handleFilterClick("all")}><RestartAlt fontSize="small" /></IconButton></Tooltip>
                <IconButton size="small" onClick={toggleLocationFilter} color={filterByLocation ? "primary" : "default"}>
                  {filterByLocation ? <LocationOn fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </Stack>

              <DynamicButton route="/create-client" text="+ Client" variant="contained" sx={{ borderRadius: 1.5, px: 2, py: 0.8, fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap' }} />
            </Box>
          </Box>
        </Paper>
        <Box sx={{ p: 1, px: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
          <Typography variant="caption" fontWeight={800} color="text.secondary" noWrap>
            VIEW: {carePlanFilter.replace('_', ' ').toUpperCase()}
          </Typography>
        </Box>
        
        <Box sx={{ flexGrow: 1, overflow: 'auto', width: '100%' }}>
          {isFetching && data?.results?.length > 0 ? (
            <ClientListSkeleton
              count={5} />
          ) : (
            <ClientList 
              data={data?.results || []} 
              currentPage={currentPage} 
              handlePageChange={handlePageChange} 
              totalRecords={totalRecords} 
            />
          )}
        </Box>

        <Box sx={{ p: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, display: 'flex', justifyContent: 'center', bgcolor: theme.palette.background.paper }}>
          <Pagination 
            count={totalPages > 0 ? totalPages : 1} 
            page={currentPage} 
            onChange={handlePageChange} 
            size="small" 
            color="primary" 
            showFirstButton 
            showLastButton
          />
        </Box>
      </Box>
    </Fade>
  );
};

export default ClientsScreen;